import React, { useEffect, useRef } from 'react';

import useSearchCustomers from './hooks/useSearchCustomers';
import useDownloadReportForm from '../../hooks/useDownloadReportForm';

import SearchInput from '../../../_ui/Form/SearchInput/SearchInput';
import Typography from '../../../_ui/Typography/Typography';
import Tag from '../../../_ui/Tag/Tag';
import SearchDropDown from '../components/SearchDropDown';

type UseDownloadReportFormReturnType = ReturnType<typeof useDownloadReportForm>;

interface Props extends Pick<UseDownloadReportFormReturnType, 'formData' | 'setFormData'> {
    errorMessage?: string;
    className?: string;
}

export default function CustomerSelector({ formData, setFormData, errorMessage, className = '' }: Props) {
    const {
        searchTerm,
        setSearchTerm,
        dropdownOpen,
        setDropdownOpen,
        isLoading,
        customers,
    } = useSearchCustomers();

    const searchInputWrapperRef = useRef<HTMLDivElement | null>(null);

    const setCustomer = (id: number, name: string) => {
        const isAlreadySet = formData.customers.find((customer) => (
            customer.id === id
        ));

        setFormData({
            ...formData,
            customers: isAlreadySet
                ? formData.customers.filter((customer) => (
                    customer.id !== id
                ))
                : [...formData.customers, {
                    id,
                    name,
                }],
        })
    }

    const formStateCustomerIds = formData.customers.map(({ id }) => id);
    const filteredCustomers = customers.filter(({ id }) => (
        !formStateCustomerIds.includes(id!)
    ));

    // On input focus, display the dropdown if the dropdown is hidden and there's a search term.
    const handleInputFocus = () => {
        console.log('focus');

        if (!dropdownOpen && searchTerm) {
            setDropdownOpen(true)
        }
    }

    // Closes the dropdown if the user clicks anywhere else on the page that's not part of the input or
    // dropdown.
    useEffect(() => {
        const handleCloseDropdownClick = (e: MouseEvent) => {
            const { target } = e;

            // If there's no input wrapper ref, no target clicked, or the target is not an HTML node.
            if (!searchInputWrapperRef.current || !target || target instanceof HTMLElement === false) {
                return;
            }

            // If the user clicked an item that's not inside the input ref, then we want to hide the dropdown.
            if (searchInputWrapperRef.current.contains(target) === false) {
                setDropdownOpen(false);
            }
        }

        // Only add the click event listener if there's a search term.
        if (searchTerm) {
            document.body.addEventListener('click', handleCloseDropdownClick, true);
        }

        // Always run the cleanup, will fail silently if there is no click event to remove.
        return () => {
            document.body.removeEventListener('click', handleCloseDropdownClick, true);
        }
    }, [searchTerm, setDropdownOpen]);

    return (
        <fieldset className={`space-y-050 ${className}`}>
            <legend className="type-small-caps">Customers</legend>
            <div ref={searchInputWrapperRef} className="relative">
                <SearchInput
                    name="customer-search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value || '')}
                    placeholder="Search by customer name"
                    onFocus={handleInputFocus}
                />
                {dropdownOpen ? (
                    <SearchDropDown
                        isLoading={isLoading}
                        items={filteredCustomers}
                        noneFoundMessage={`No results found for ${searchTerm}`}
                        onSelect={setCustomer}
                    />
                ) : null}
            </div>
            {errorMessage
                ? <Typography as="div" variant="meta" color="error">{errorMessage}</Typography>
                : null}
            {formData.customers.length ? (
                <div className="flex gap-025 flex-wrap max-w-[19rem]">
                    {formData.customers.map((customer) => (
                        <Tag
                            key={customer.id}
                            size="small"
                            onClick={() => setCustomer(customer.id!, customer.name)}
                            icon="cross"
                            title={`Remove ${customer.name}`}
                        >
                            {customer.name}
                        </Tag>
                    ))}
                </div>
            ) : null}
        </fieldset>
    )
}
